import { Button, IconButton } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Classe que eleva o dropdown de um `SelectInput`/`Autocomplete` acima do dialog.
 *
 * Aplique em `custom={{ content: SELECT_ABOVE_MODAL }}` — **não** em
 * `innerProps.content.className`, que substitui a className do ADS e apaga fundo,
 * borda e sombra do portal.
 *
 * O valor alto vem do precedente do projeto (`UploadAttachmentModal`): o overlay
 * e o conteúdo do dialog ficam em `z-50`, e o portal do select é irmão deles no
 * `body`.
 */
export const SELECT_ABOVE_MODAL = 'z-[99999]';

/** Props de `PlanFormModal`. */
export interface PlanFormModalProps {
  /** Controla a visibilidade. */
  readonly open: boolean;
  /** Título no cabeçalho. */
  readonly title: string;
  /** Texto de contexto abaixo do título. */
  readonly description?: string | undefined;
  /** Rótulo da ação primária. */
  readonly confirmLabel: string;
  /** `true` enquanto há request em voo. */
  readonly isPending?: boolean | undefined;
  /** Confirma o formulário. */
  readonly onConfirm: () => void;
  /** Fecha sem confirmar. */
  readonly onClose: () => void;
  /** Campos do formulário. */
  readonly children: ReactNode;
}

/**
 * Casca de modal de formulário da edição de plano.
 *
 * Usa `@radix-ui/react-dialog` direto, não o `ModalDialog` do ADS. O
 * `ModalDialog` é um diálogo de **confirmação** — título, descrição e duas ações
 * — e o `slot` dele é escape para detalhe, não corpo de formulário. Três
 * consequências práticas motivaram a troca: o corpo não tinha controle de
 * rolagem (formulário com campo condicional estourava a viewport), não havia
 * `<form>` de verdade (Enter não submetia) e o portal dos selects brigava com o
 * empilhamento do dialog.
 *
 * O projeto já tinha esse precedente em `UploadAttachmentModal` e
 * `DisciplineFormModal`, ambos com o mesmo boilerplate copiado. Esta casca
 * centraliza o padrão para os modais desta feature.
 */
export function PlanFormModal({
  open,
  title,
  description,
  confirmLabel,
  isPending,
  onConfirm,
  onClose,
  children,
}: PlanFormModalProps) {
  const { t } = useTranslation('individualPayment');

  return (
    <Dialog.Root
      open={open}
      // `modal={false}` é obrigatório aqui: o dropdown do `Autocomplete` do ADS é
      // portado para o `body`, e com o dialog em modo modal o Radix bloqueia
      // pointer events fora do `Dialog.Content` — as opções apareciam mas não
      // eram clicáveis. Mesmo motivo do `CityFormModal`.
      modal={false}
      onOpenChange={next => {
        if (!next && !isPending) onClose();
      }}
    >
      <Dialog.Portal>
        {/* Backdrop manual: com `modal={false}` o Radix não renderiza o Dialog.Overlay. */}
        <div
          aria-hidden="true"
          className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft"
        />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
          // Com `modal={false}` o clique fora não é interceptado pelo Radix; sem
          // isto, interagir com o dropdown portado fecharia o dialog.
          onInteractOutside={event => event.preventDefault()}
        >
          <div className="flex items-start justify-between gap-mini border-b-hairline border-ctx-layout-border px-xs py-mini">
            <span className="flex flex-col gap-quark">
              <Dialog.Title className="ts-heading-sm text-ctx-content-title">{title}</Dialog.Title>
              {description ? (
                <span className="ts-paragraph-xxs text-ctx-content-placeholder">{description}</span>
              ) : null}
            </span>
            <Dialog.Close asChild>
              {/* Nome distinto do botão secundário: dois controles com o mesmo
                  nome acessível deixam o fechar indistinguível para leitor de tela. */}
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                disabled={isPending}
                ariaLabel={t('planEdit.close')}
              />
            </Dialog.Close>
          </div>

          {/* `<form>` de verdade: Enter submete, o que o `slot` do ModalDialog não dava. */}
          <form
            className="flex min-h-none flex-col"
            onSubmit={event => {
              event.preventDefault();
              onConfirm();
            }}
          >
            {/* `gap-mini` (16px), não o `gap-xs` (32px) do precedente: estes são
                formulários curtos, e a folga dobrada empurrava o rodapé para fora
                da vista. A rolagem aqui é segura porque os dropdowns são portados
                para o `body` — nenhum ancestral com overflow os recorta. */}
            <div className="flex flex-col gap-mini overflow-y-auto px-xs py-mini">{children}</div>

            <div className="flex items-center justify-end gap-micro border-t-hairline border-ctx-layout-border px-xs py-mini">
              <Button hierarchy="secondary" size="md" disabled={isPending} onClick={onClose}>
                {t('planEdit.footer.cancel')}
              </Button>
              <Button
                hierarchy="primary"
                size="md"
                type="submit"
                isLoading={isPending === true}
                disabled={isPending === true}
              >
                {confirmLabel}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
